"use client";

import React, { useState, useEffect } from "react";
import { Settings, ChevronDown, ChevronUp, Save, Loader2, Monitor } from "lucide-react";
import { Group, DEFAULT_GROUP_CONFIG } from "@/types/group.interface";
import Api from "@/lib/api";
import { Segment } from "@/types/segment.interface";

interface GenerationSettingsPanelProps {
    group: Group | null;
    setCurrentGroup: React.Dispatch<React.SetStateAction<Group | null>>;
    setGroups: React.Dispatch<React.SetStateAction<Group[]>>;
    setSegments: React.Dispatch<React.SetStateAction<Segment[]>>;
}

const ASPECT_RATIOS = ["9:16", "16:9", "1:1", "4:3", "3:4"];
const MODELS = [
    { value: "veo-3.1-generate-preview", label: "Veo 3.1 (Preview)" },
    { value: "veo-3-generate-preview", label: "Veo 3.0 (Preview)" },
    { value: "veo-2.0-generate-001", label: "Veo 2.0" },
];

export const GenerationSettingsPanel: React.FC<GenerationSettingsPanelProps> = ({
    group,
    setCurrentGroup,
    setGroups,
    setSegments
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [form, setForm] = useState({
        model: group?.model ?? DEFAULT_GROUP_CONFIG.model,
        aspectRatio: group?.aspectRatio ?? DEFAULT_GROUP_CONFIG.aspectRatio,
        includeAudio: group?.includeAudio ?? DEFAULT_GROUP_CONFIG.includeAudio,
        globalConstraints: group?.globalConstraints ?? DEFAULT_GROUP_CONFIG.globalConstraints,
        negativePromptGlobal: group?.negativePromptGlobal ?? DEFAULT_GROUP_CONFIG.negativePromptGlobal,
        structuralAnchors: group?.structuralAnchors ?? DEFAULT_GROUP_CONFIG.structuralAnchors ?? "",
        videoFilenamePrefix: group?.videoFilenamePrefix ?? DEFAULT_GROUP_CONFIG.videoFilenamePrefix,
    });

    const [aiForm, setAiForm] = useState({
        title: "",
        sellingPoints: ""
    });
    const [isGeneratingScript, setIsGeneratingScript] = useState(false);

    // 当 group 变化时同步表单
    useEffect(() => {
        if (group) {
            setForm({
                model: group.model,
                aspectRatio: group.aspectRatio,
                includeAudio: group.includeAudio,
                globalConstraints: group.globalConstraints,
                negativePromptGlobal: group.negativePromptGlobal,
                structuralAnchors: group.structuralAnchors ?? "",
                videoFilenamePrefix: group.videoFilenamePrefix,
            });
        }
    }, [group?.id]);

    const hasChanges = group && (
        form.model !== group.model ||
        form.aspectRatio !== group.aspectRatio ||
        form.includeAudio !== group.includeAudio ||
        form.globalConstraints !== group.globalConstraints ||
        form.negativePromptGlobal !== group.negativePromptGlobal ||
        form.structuralAnchors !== (group.structuralAnchors ?? "") ||
        form.videoFilenamePrefix !== group.videoFilenamePrefix
    );

    const handleUpdateGroup = async (data: Partial<Group>) => {
        if (!group) return;
        try {
            const updated = await Api.groups.update(group.id, data);
            setCurrentGroup(updated);
            setGroups(prev => prev.map(g => g.id === updated.id ? updated : g));
            if (updated.segments) setSegments(updated.segments);
        } catch (error) { console.error("Update group error:", error); }
    };

    const handleSave = async () => {
        if (!group) return;
        setIsSaving(true);
        try {
            await handleUpdateGroup(form);
        } finally {
            setIsSaving(false);
        }
    };

    const handleGenerateScript = async () => {
        if (!group || !group.project?.apiKey) {
            alert("请先在配置管理中设置 API Key");
            return;
        }
        if (!aiForm.title || !aiForm.sellingPoints) {
            alert("请填写商品标题和卖点");
            return;
        }

        setIsGeneratingScript(true);
        try {
            const res = await fetch("/api/generate/script", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    groupId: group.id,
                    title: aiForm.title,
                    sellingPoints: aiForm.sellingPoints,
                    apiKey: group.project.apiKey
                })
            });
            const data = await res.json();
            if (res.ok) {
                const refreshed = await Api.groups.get(group.id);
                setCurrentGroup(refreshed);
                setGroups(prev => prev.map(g => g.id === refreshed.id ? refreshed : g));
                if (refreshed.segments) setSegments(refreshed.segments);
                alert("脚本生成成功！已为您自动填充片段。");
            } else {
                alert(`生成失败: ${data.error || "未知错误"}`);
            }
        } catch (error) {
            console.error("Generate script error:", error);
            alert("生成过程中发生错误");
        } finally {
            setIsGeneratingScript(false);
        }
    };

    if (!group) return null;

    return (
        <div className="bg-white dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-800 rounded-none overflow-hidden mb-4 transition-colors">
            {/* 折叠标题栏 */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex justify-between items-center p-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <Monitor size={14} className="text-emerald-400" />
                    <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">
                        生成配置 (Generation Settings)
                    </span>
                    {hasChanges && (
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" title="有未保存的更改" />
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {hasChanges && !isExpanded && (
                        <span className="text-[10px] text-emerald-500 font-medium">未保存更改</span>
                    )}
                    {isExpanded ? <ChevronUp size={14} className="text-zinc-500" /> : <ChevronDown size={14} className="text-zinc-500" />}
                </div>
            </button>

            {isExpanded && (
                <div className="p-4 pt-0 space-y-4 border-t border-zinc-800/50">
                    {/* 模型 + 画面比例 */}
                    <div className="grid grid-cols-2 gap-3 mt-4">
                        <div>
                            <label className="text-[10px] text-zinc-500 font-bold uppercase mb-1 block">视频模型</label>
                            <select
                                className="w-full bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700/50 rounded-none px-2 py-1.5 text-xs text-zinc-800 dark:text-zinc-200 outline-none focus:border-emerald-500/50 transition-colors"
                                value={form.model}
                                onChange={e => setForm(prev => ({ ...prev, model: e.target.value }))}
                            >
                                {MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] text-zinc-500 font-bold uppercase mb-1 block">画面比例</label>
                            <select
                                className="w-full bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700/50 rounded-none px-2 py-1.5 text-xs text-zinc-800 dark:text-zinc-200 outline-none focus:border-emerald-500/50 transition-colors"
                                value={form.aspectRatio}
                                onChange={e => setForm(prev => ({ ...prev, aspectRatio: e.target.value }))}
                            >
                                {ASPECT_RATIOS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* 文件名前缀 */}
                    <div className="space-y-1">
                        <label className="text-[10px] text-zinc-500 font-bold uppercase block ml-1">视频文件名前缀 (Filename Prefix)</label>
                        <input
                            className="w-full bg-zinc-50 dark:bg-zinc-800/30 border border-zinc-200 dark:border-zinc-800/50 rounded-none px-3 py-2 text-xs text-zinc-700 dark:text-zinc-400 outline-none focus:border-emerald-500/30 transition-colors"
                            value={form.videoFilenamePrefix}
                            onChange={e => setForm(prev => ({ ...prev, videoFilenamePrefix: e.target.value }))}
                            placeholder="例如: veo, scene1, projectA..."
                        />
                        <p className="text-[9px] text-zinc-600 ml-1">最终格式: {form.videoFilenamePrefix}_[序号]_[时间戳].mp4</p>
                    </div>

                    {/* 包含音频 */}
                    <div className="flex items-center justify-between py-1 px-1">
                        <label className="text-[10px] text-zinc-500 font-bold uppercase">包含音频 (Audio)</label>
                        <button
                            onClick={() => setForm(prev => ({ ...prev, includeAudio: !prev.includeAudio }))}
                            className={`relative w-8 h-4 rounded-none transition-colors ${form.includeAudio ? "bg-emerald-600" : "bg-zinc-700"}`}
                        >
                            <span className={`absolute top-0.5 w-3 h-3 rounded-none bg-white transition-transform ${form.includeAudio ? "translate-x-4" : "translate-x-0.5"}`} />
                        </button>
                    </div>

                    {/* 全局镜头约束 */}
                    <div className="space-y-1">
                        <label className="text-[10px] text-zinc-500 font-bold uppercase block ml-1">全局镜头约束 (Global Constraints)</label>
                        <textarea
                            className="w-full bg-zinc-50 dark:bg-zinc-800/30 border border-zinc-200 dark:border-zinc-800/50 rounded-none px-3 py-2 text-xs text-zinc-700 dark:text-zinc-300 outline-none focus:border-emerald-500/30 transition-colors resize-none h-20 custom-scrollbar"
                            value={form.globalConstraints}
                            onChange={e => setForm(prev => ({ ...prev, globalConstraints: e.target.value }))}
                            placeholder="透视：固定 45 度角..."
                        />
                    </div>

                    {/* 全局负向提示词 */}
                    <div className="space-y-1">
                        <label className="text-[10px] text-zinc-500 font-bold uppercase block ml-1">全局负向提示词 (Global Negative Prompt)</label>
                        <input
                            className="w-full bg-zinc-50 dark:bg-zinc-800/30 border border-zinc-200 dark:border-zinc-800/50 rounded-none px-3 py-2 text-xs text-zinc-700 dark:text-zinc-400 outline-none focus:border-emerald-500/30 transition-colors"
                            value={form.negativePromptGlobal}
                            onChange={e => setForm(prev => ({ ...prev, negativePromptGlobal: e.target.value }))}
                            placeholder="外观发生变化"
                        />
                    </div>

                    {/* 结构锚点 */}
                    <div className="space-y-1">
                        <label className="text-[10px] text-zinc-500 font-bold uppercase block ml-1">结构锚点 (Structural Anchors)</label>
                        <textarea
                            className="w-full bg-zinc-50 dark:bg-zinc-800/30 border border-zinc-200 dark:border-zinc-800/50 rounded-none px-3 py-2 text-xs text-zinc-700 dark:text-zinc-300 outline-none focus:border-emerald-500/30 transition-colors resize-none h-16 custom-scrollbar"
                            value={form.structuralAnchors}
                            onChange={e => setForm(prev => ({ ...prev, structuralAnchors: e.target.value }))}
                            placeholder="容器位于底部，喷头指向前方..."
                        />
                    </div>

                    {/* TikTok AI 脚本生成 */}
                    <div className="pt-4 border-t border-zinc-800/50 space-y-3">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest">TikTok AI 脚本助理</span>
                            <span className="px-1 py-0.5 bg-blue-500/10 text-blue-500 text-[8px] font-bold rounded">BETA</span>
                        </div>

                        <div className="space-y-2">
                            <input
                                className="w-full bg-zinc-50 dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-800/50 rounded-none px-3 py-2 text-xs text-zinc-700 dark:text-zinc-200 outline-none focus:border-blue-500/30 transition-colors"
                                value={aiForm.title}
                                onChange={e => setAiForm(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="输入商品名称 (如: 高端智能保温杯)"
                            />
                            <textarea
                                className="w-full bg-zinc-50 dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-800/50 rounded-none px-3 py-2 text-xs text-zinc-700 dark:text-zinc-300 outline-none focus:border-blue-500/30 transition-colors resize-none h-20 custom-scrollbar"
                                value={aiForm.sellingPoints}
                                onChange={e => setAiForm(prev => ({ ...prev, sellingPoints: e.target.value }))}
                                placeholder="输入核心卖点 (如: 24小时保温, 316医用不锈钢, 极简设计...)"
                            />
                        </div>

                        <button
                            onClick={handleGenerateScript}
                            disabled={isGeneratingScript || !aiForm.title || !aiForm.sellingPoints}
                            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-none text-[10px] font-black uppercase tracking-tighter transition-all ${isGeneratingScript || !aiForm.title || !aiForm.sellingPoints
                                ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-600 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/20"
                                }`}
                        >
                            {isGeneratingScript ? <Loader2 size={12} className="animate-spin" /> : <Settings size={12} />}
                            {isGeneratingScript ? "正在创作爆款脚本..." : "一键生成 TikTok 挂车脚本"}
                        </button>
                        <p className="text-[9px] text-zinc-500 text-center">AI 将自动生成 5 个分段提示词并填充到下方时间轴</p>
                    </div>

                    {/* 保存按钮 */}
                    <div className="flex justify-end pt-2">
                        <button
                            onClick={handleSave}
                            disabled={isSaving || !hasChanges}
                            className={`flex items-center gap-2 px-4 py-2 rounded-none text-xs font-bold transition-all ${!hasChanges
                                ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-600 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-500 text-white"
                                }`}
                        >
                            {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                            保存配置 (Save Config)
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
